/**
 * AILogs — /admin/ai-logs
 *
 * Wave 6 Batch 3 · Cluster A Turn 2 — chrome sweep onto `--ds-*` tokens.
 * Anchor: CreditEngineEvents (Wave 5-Late).
 *
 *   • AdminPageHelp lifted out of `actions` (was mis-nested).
 *   • `_primitives.Section` shells for filter + main table.
 *   • `_primitives.SelectInput` / `TextInput` for filters + review form.
 *   • `_primitives.GhostButton` / `AccentButton` for Export / Refresh /
 *     Save review.
 *   • REVIEW_COLOR + RESPONSE_COLOR pastel maps REMOVED. `Pill` now
 *     emits a DS `Badge` with `shape="sharp"` + REVIEW_TONE /
 *     RESPONSE_TONE maps.
 *   • Dense hairline table `px-4 py-1.5` + `isLast` guard.
 *   • Modal chrome swept to `--ds-*`; backdrop `rgba(27,26,22,0.55)`.
 *
 * Every testid, API path, filter payload preserved BYTE-EXACT.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Download, ExternalLink, RefreshCw, Search, X } from "lucide-react";
import { toast } from "sonner";
import AdminLayout from "@/admin/AdminLayout";
import AdminPageHelp from "@/design-system/components/AdminPageHelp";
import { Badge } from "@/design-system/components/Badge";
import { Heading } from "@/design-system/components/Heading";
import { api, formatApiError } from "@/api/client";
import { Section, SelectInput, TextInput, GhostButton, AccentButton } from "@/admin/_primitives";

const REVIEW_STATUSES = ["unreviewed", "approved", "flagged", "needs_attention"];
const RESPONSE_STATUSES = ["success", "failed", "partial"];

const REVIEW_TONE = {
  unreviewed:      "neutralMuted",
  approved:        "successMuted",
  flagged:         "danger",
  needs_attention: "warning",
};
const RESPONSE_TONE = {
  success: "successMuted",
  partial: "warning",
  failed:  "danger",
};

function TonePill({ value, tone }) {
  if (!value) return <span style={{ color: "var(--ds-color-ink-subtle)" }}>—</span>;
  return (
    <Badge tone={tone || "neutralMuted"} size="sm" shape="sharp" className="uppercase tracking-[0.1em]">
      {String(value).replace(/_/g, " ")}
    </Badge>
  );
}

export default function AdminAILogs() {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [provider, setProvider] = useState("");
  const [feature, setFeature] = useState("");
  const [responseStatus, setResponseStatus] = useState("");
  const [reviewStatus, setReviewStatus] = useState("");
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [offset, setOffset] = useState(0);
  const [openId, setOpenId] = useState(null);

  useEffect(() => { const t = setTimeout(() => setDebouncedSearch(search), 250); return () => clearTimeout(t); }, [search]);

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (provider) params.set("provider", provider);
      if (feature) params.set("feature", feature);
      if (responseStatus) params.set("response_status", responseStatus);
      if (reviewStatus) params.set("admin_review_status", reviewStatus);
      params.set("limit", "50");
      params.set("offset", String(offset));
      const { data } = await api.get(`/admin/ai-audit-logs?${params.toString()}`);
      setItems(data?.items || []);
      setTotal(data?.total || 0);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  }, [provider, feature, responseStatus, reviewStatus, offset]);

  useEffect(() => { reload(); }, [reload]);

  const visible = useMemo(() => {
    const q = debouncedSearch.trim().toLowerCase();
    if (!q) return items;
    return items.filter((r) => (`${r.user_email || ""} ${r.model || ""} ${r.feature || ""}`).toLowerCase().includes(q));
  }, [items, debouncedSearch]);

  const featureOptions = useMemo(() => Array.from(new Set(items.map((i) => i.feature).filter(Boolean))).sort(), [items]);
  const providerOptions = useMemo(() => Array.from(new Set(items.map((i) => i.provider).filter(Boolean))).sort(), [items]);

  function exportCsv() {
    const params = new URLSearchParams();
    if (provider) params.set("provider", provider);
    if (feature) params.set("feature", feature);
    if (responseStatus) params.set("response_status", responseStatus);
    if (reviewStatus) params.set("admin_review_status", reviewStatus);
    const base = `${process.env.REACT_APP_BACKEND_URL || ""}/api/admin/ai-audit-logs/export.csv`;
    window.open(`${base}?${params.toString()}`, "_blank");
  }

  return (
    <AdminLayout
      title="AI logs"
      subtitle={`${total} AI audit records (showing ${visible.length})`}
      actions={
        <div className="flex items-center gap-2">
          <GhostButton data-testid="ai-logs-export" onClick={exportCsv}>
            <Download className="w-3 h-3" /> Export CSV
          </GhostButton>
          <GhostButton data-testid="ai-logs-refresh" onClick={reload}>
            <RefreshCw className="w-3 h-3" /> Refresh
          </GhostButton>
        </div>
      }
    >
      <div className="mb-3 max-w-3xl">
        <AdminPageHelp
          title="AI Logs"
          purpose="Read-only audit trail of every LLM call made by the platform (credit extraction, roadmap analysis, dispute drafting)."
          configures="Nothing — this page is read-only. Filter by provider, feature, status, and review state."
          impact="No user-facing impact; used to audit AI usage, spot failures, and review flagged outputs."
          safety="Read-only. Records are retained indefinitely for compliance review."
        />
      </div>

      <Section title="Filter" testid="ai-logs-filter-section">
        <div className="grid md:grid-cols-[1fr_repeat(4,auto)] gap-2 items-end">
          <label className="block relative">
            <Search
              className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none"
              style={{ color: "var(--ds-color-ink-subtle)" }}
              aria-hidden="true"
            />
            <TextInput
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search user / model / feature…"
              data-testid="ai-logs-search"
              className="w-full pl-9"
            />
          </label>
          <SelectInput
            value={feature}
            onChange={(e) => { setOffset(0); setFeature(e.target.value); }}
            data-testid="ai-logs-filter-feature"
          >
            <option value="">All features</option>
            {featureOptions.map((f) => <option key={f} value={f}>{f}</option>)}
          </SelectInput>
          <SelectInput
            value={provider}
            onChange={(e) => { setOffset(0); setProvider(e.target.value); }}
            data-testid="ai-logs-filter-provider"
          >
            <option value="">All providers</option>
            {providerOptions.map((p) => <option key={p} value={p}>{p}</option>)}
          </SelectInput>
          <SelectInput
            value={responseStatus}
            onChange={(e) => { setOffset(0); setResponseStatus(e.target.value); }}
            data-testid="ai-logs-filter-status"
          >
            <option value="">All statuses</option>
            {RESPONSE_STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
          </SelectInput>
          <SelectInput
            value={reviewStatus}
            onChange={(e) => { setOffset(0); setReviewStatus(e.target.value); }}
            data-testid="ai-logs-filter-review"
          >
            <option value="">All review states</option>
            {REVIEW_STATUSES.map((r) => <option key={r} value={r}>{r}</option>)}
          </SelectInput>
        </div>
      </Section>

      {loading ? (
        <div className="text-[13px] py-6 text-center" style={{ color: "var(--ds-color-ink-muted)" }}>
          Loading…
        </div>
      ) : (
        <Section
          title="AI audit records"
          testid="ai-logs-section"
          bodyClassName="p-0"
          badge={
            <span
              className="text-[11px] uppercase tracking-[0.14em] tabular-nums"
              style={{ color: "var(--ds-color-ink-muted)" }}
            >
              {visible.length} showing · {total} total
            </span>
          }
        >
          <div className="overflow-x-auto" data-testid="ai-logs-table">
            <table className="w-full text-[13px]">
              <thead>
                <tr
                  className="text-left text-[10px] uppercase tracking-[0.16em] font-semibold"
                  style={{
                    color: "var(--ds-color-ink-subtle)",
                    background: "var(--ds-color-admin-surface-muted)",
                    borderBottom: "1px solid var(--ds-color-admin-border)",
                  }}
                >
                  <th className="px-4 py-2">When</th>
                  <th className="px-4 py-2">Feature</th>
                  <th className="px-4 py-2">Provider / Model</th>
                  <th className="px-4 py-2">Status</th>
                  <th className="px-4 py-2 text-right">Latency</th>
                  <th className="px-4 py-2">Review</th>
                  <th className="px-4 py-2">User</th>
                  <th className="px-4 py-2 text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {visible.map((row, idx) => {
                  const isLast = idx === visible.length - 1;
                  return (
                    <tr
                      key={row.id}
                      data-testid={`ai-logs-row-${row.id}`}
                      className="cursor-pointer transition-colors"
                      onClick={() => setOpenId(row.id)}
                      style={{
                        borderBottom: isLast ? "none" : "1px solid var(--ds-color-admin-border)",
                        transitionDuration: "var(--ds-duration-fast)",
                      }}
                      onMouseEnter={(e) =>
                        (e.currentTarget.style.background = "var(--ds-color-admin-surface-muted)")
                      }
                      onMouseLeave={(e) =>
                        (e.currentTarget.style.background = "transparent")
                      }
                    >
                      <td
                        className="px-4 py-1.5 text-[12px] whitespace-nowrap tabular-nums font-mono"
                        style={{ color: "var(--ds-color-ink-muted)" }}
                      >
                        {row.created_at?.slice ? row.created_at.slice(0, 19).replace("T", " ") : "—"}
                      </td>
                      <td className="px-4 py-1.5 text-[12px]" style={{ color: "var(--ds-color-ink-muted)" }}>
                        {row.feature}
                      </td>
                      <td className="px-4 py-1.5">
                        <div className="text-[12px]" style={{ color: "var(--ds-color-ink)" }}>{row.provider}</div>
                        <div className="text-[10px] font-mono" style={{ color: "var(--ds-color-ink-muted)" }}>
                          {row.model}
                        </div>
                      </td>
                      <td className="px-4 py-1.5">
                        <TonePill value={row.response_status} tone={RESPONSE_TONE[row.response_status]} />
                      </td>
                      <td
                        className="px-4 py-1.5 text-[12px] text-right tabular-nums font-mono"
                        style={{ color: "var(--ds-color-ink-muted)" }}
                      >
                        {row.latency_ms != null ? `${row.latency_ms}ms` : "—"}
                      </td>
                      <td className="px-4 py-1.5">
                        <TonePill value={row.admin_review_status} tone={REVIEW_TONE[row.admin_review_status]} />
                      </td>
                      <td
                        className="px-4 py-1.5 text-[12px] truncate max-w-[200px]"
                        style={{ color: "var(--ds-color-ink-muted)" }}
                      >
                        {row.user_email || "—"}
                      </td>
                      <td className="px-4 py-1.5 text-right">
                        <span
                          className="text-[11px] uppercase tracking-[0.12em] font-semibold"
                          style={{ color: "var(--ds-color-accent-strong)" }}
                        >
                          Open
                        </span>
                      </td>
                    </tr>
                  );
                })}
                {visible.length === 0 && (
                  <tr>
                    <td
                      colSpan="8"
                      className="px-4 py-8 text-center text-[13px]"
                      style={{ color: "var(--ds-color-ink-muted)" }}
                    >
                      No AI logs match.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </Section>
      )}

      <div
        className="flex items-center justify-between mt-2 text-[11px] tabular-nums"
        style={{ color: "var(--ds-color-ink-muted)" }}
      >
        <span>Page offset {offset}</span>
        <div className="flex gap-1.5">
          <GhostButton
            onClick={() => setOffset(Math.max(0, offset - 50))}
            disabled={offset === 0}
            className="!py-1 !text-[11px]"
          >
            Prev
          </GhostButton>
          <GhostButton
            onClick={() => setOffset(offset + 50)}
            disabled={offset + 50 >= total}
            className="!py-1 !text-[11px]"
          >
            Next
          </GhostButton>
        </div>
      </div>

      {openId && <AILogDetail id={openId} onClose={() => { setOpenId(null); reload(); }} />}
    </AdminLayout>
  );
}

function AILogDetail({ id, onClose }) {
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [reviewStatus, setReviewStatus] = useState("");
  const [notes, setNotes] = useState("");

  useEffect(() => {
    let cancel = false;
    setLoading(true);
    api.get(`/admin/ai-audit-logs?limit=1&offset=0`).catch(() => {});
    // The list endpoint returns rows but no per-id endpoint; refetch the row
    // from the list cache via filter.
    (async () => {
      try {
        // Pull the latest 200 and find the row. Phase 8b will add a /by-id endpoint.
        const { data } = await api.get(`/admin/ai-audit-logs?limit=200`);
        const row = (data.items || []).find((r) => r.id === id);
        if (!cancel && row) {
          setDetail(row);
          setReviewStatus(row.admin_review_status || "unreviewed");
          setNotes(row.admin_notes || "");
        }
      } catch (e) {
        toast.error(formatApiError(e));
      } finally {
        if (!cancel) setLoading(false);
      }
    })();
    return () => { cancel = true; };
  }, [id]);

  async function save() {
    setSaving(true);
    try {
      await api.patch(`/admin/ai-audit-logs/${id}`, {
        admin_review_status: reviewStatus,
        admin_notes: notes,
      });
      toast.success("Review saved");
      onClose();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{ background: "rgba(27, 26, 22, 0.55)" }}
      onClick={onClose}
    >
      <div
        data-testid="ai-logs-modal"
        className="w-full max-w-2xl max-h-[90vh] overflow-y-auto rounded-[var(--ds-radius-card)]"
        style={{
          background: "var(--ds-color-surface)",
          border: "1px solid var(--ds-color-admin-border)",
          boxShadow: "0 1px 0 0 var(--ds-color-admin-border)",
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <header
          className="sticky top-0 flex items-center justify-between px-5 py-3.5 z-10 border-b"
          style={{
            background: "var(--ds-color-surface)",
            borderColor: "var(--ds-color-admin-border)",
          }}
        >
          <div className="flex items-center gap-3 min-w-0">
            <button
              type="button"
              onClick={onClose}
              data-testid="ai-logs-modal-back"
              className="inline-flex items-center gap-1 text-[12px] uppercase tracking-[0.12em] transition-colors"
              style={{ color: "var(--ds-color-ink-muted)" }}
              onMouseEnter={(e) => (e.currentTarget.style.color = "var(--ds-color-ink)")}
              onMouseLeave={(e) => (e.currentTarget.style.color = "var(--ds-color-ink-muted)")}
            >
              <ArrowLeft className="w-3.5 h-3.5" /> Back to list
            </button>
            <div className="min-w-0">
              <div
                className="text-[10px] uppercase tracking-[0.18em] font-semibold"
                style={{ color: "var(--ds-color-accent-strong)" }}
              >
                AI audit log
              </div>
              <Heading level={2} size="md" weight="semibold" className="truncate">
                {detail?.feature || "…"} · {detail?.provider}
              </Heading>
            </div>
          </div>
          <button
            onClick={onClose}
            className="transition-colors"
            style={{ color: "var(--ds-color-ink-muted)" }}
            onMouseEnter={(e) => (e.currentTarget.style.color = "var(--ds-color-ink)")}
            onMouseLeave={(e) => (e.currentTarget.style.color = "var(--ds-color-ink-muted)")}
          >
            <X className="w-5 h-5" />
          </button>
        </header>
        <div className="p-5 space-y-4">
          {loading && <div className="text-sm" style={{ color: "var(--ds-color-ink-muted)" }}>Loading…</div>}
          {detail && (
            <>
              <Meta label="Created at" value={detail.created_at?.slice ? detail.created_at.replace("T", " ").slice(0, 19) : detail.created_at} />
              <Meta label="Model" value={detail.model} />
              <Meta label="Response status" value={detail.response_status} />
              <Meta label="Latency" value={detail.latency_ms != null ? `${detail.latency_ms}ms` : "—"} />
              <Meta label="Tokens (prompt / completion)" value={`${detail.prompt_tokens ?? "—"} / ${detail.completion_tokens ?? "—"}`} />
              <Meta label="User" value={detail.user_email} />
              {detail.error_message && (
                <div>
                  <div
                    className="text-[10px] uppercase tracking-[0.12em] font-semibold mb-1"
                    style={{ color: "var(--ds-color-ink-subtle)" }}
                  >
                    Error
                  </div>
                  <div
                    className="rounded-[var(--ds-radius-input)] p-2 text-xs"
                    style={{
                      background: "var(--ds-color-danger-soft)",
                      border: "1px solid var(--ds-color-danger)",
                      color: "var(--ds-color-danger)",
                    }}
                  >
                    {detail.error_message}
                  </div>
                </div>
              )}
              <div>
                <div
                  className="text-[10px] uppercase tracking-[0.12em] font-semibold mb-1"
                  style={{ color: "var(--ds-color-ink-subtle)" }}
                >
                  Request summary
                </div>
                <pre
                  className="rounded-[var(--ds-radius-input)] p-3 text-xs whitespace-pre-wrap leading-relaxed font-mono"
                  style={{
                    background: "var(--ds-color-admin-surface-muted)",
                    border: "1px solid var(--ds-color-admin-border)",
                    color: "var(--ds-color-ink-muted)",
                  }}
                >
                  {detail.request_summary || "—"}
                </pre>
              </div>
              {detail.source_type && (
                <Meta
                  label="Source"
                  value={
                    detail.source_id ? (
                      <Link
                        to={detail.source_type === "credit_report" ? "/admin/credit-reports" : detail.source_type === "dispute_letter" ? "/admin/letters" : "#"}
                        className="hover:underline inline-flex items-center gap-1 font-semibold"
                        style={{ color: "var(--ds-color-accent-strong)" }}
                      >
                        {detail.source_type} · {detail.source_id.slice(-8)} <ExternalLink className="w-3 h-3" />
                      </Link>
                    ) : `${detail.source_type}`
                  }
                />
              )}
              <hr style={{ borderColor: "var(--ds-color-admin-border)" }} />
              <div>
                <div
                  className="text-[10px] uppercase tracking-[0.18em] font-semibold mb-2"
                  style={{ color: "var(--ds-color-accent-strong)" }}
                >
                  Review
                </div>
                <label className="block mb-3">
                  <span
                    className="block mb-1 uppercase tracking-[0.12em] text-[10px] font-semibold"
                    style={{ color: "var(--ds-color-ink-subtle)" }}
                  >
                    Status
                  </span>
                  <SelectInput
                    value={reviewStatus}
                    onChange={(e) => setReviewStatus(e.target.value)}
                    data-testid="ai-logs-review-status"
                    className="w-full"
                  >
                    {REVIEW_STATUSES.map((r) => <option key={r} value={r}>{r}</option>)}
                  </SelectInput>
                </label>
                <label className="block mb-3">
                  <span
                    className="block mb-1 uppercase tracking-[0.12em] text-[10px] font-semibold"
                    style={{ color: "var(--ds-color-ink-subtle)" }}
                  >
                    Notes
                  </span>
                  <textarea
                    rows={3}
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                    data-testid="ai-logs-review-notes"
                    className="w-full rounded-[var(--ds-radius-input)] border bg-[var(--ds-color-surface)] px-2.5 py-1.5 text-[13px] focus:outline-none"
                    style={{
                      borderColor: "var(--ds-color-input-border)",
                      color: "var(--ds-color-ink)",
                    }}
                  />
                </label>
                <p className="text-[10px]" style={{ color: "var(--ds-color-ink-muted)" }}>
                  {detail.reviewed_by ? `Last reviewed by ${detail.reviewed_by} on ${detail.reviewed_at?.slice ? detail.reviewed_at.slice(0,19).replace("T"," ") : detail.reviewed_at}` : "Not yet reviewed."}
                </p>
                <div className="flex justify-end mt-3">
                  <AccentButton
                    onClick={save}
                    disabled={saving}
                    data-testid="ai-logs-review-save"
                  >
                    {saving ? "Saving…" : "Save review"}
                  </AccentButton>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function Meta({ label, value }) {
  return (
    <div>
      <div
        className="text-[10px] uppercase tracking-[0.12em] font-semibold"
        style={{ color: "var(--ds-color-ink-subtle)" }}
      >
        {label}
      </div>
      <div className="text-sm" style={{ color: "var(--ds-color-ink)" }}>
        {value || "—"}
      </div>
    </div>
  );
}
